import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { BRAND, RAMP_CSS } from "../brand/warmth";
import { Wordmark } from "../components/Wordmark";

/**
 * PRD 13 asks the landing page to *demonstrate* the signature moment rather
 * than describe it. This runs a real sequence of clue purchases, with the
 * candidate count collapsing and the prices falling alongside.
 */
/**
 * A real round on a real board: the hider said "somewhere in India" and these
 * are the prices, answers and counts the engine actually produces for it
 * (`npx tsx scripts/demo-sequence.ts country:IN Kochi`).
 */
const BOARD = { label: "India", places: 659 };
const DEMO = [
  {
    clue: "Are you anywhere near the sea?",
    icon: "📏",
    cost: 16,
    answer: "I'm right on the coast — under 10 km from the sea.",
    left: 63,
  },
  {
    clue: "What language do people speak around you?",
    icon: "🗣️",
    cost: 19,
    answer: "They speak Malayalam where I am.",
    left: 10,
  },
  {
    clue: "What does the land look like around you?",
    icon: "🌿",
    cost: 3,
    answer: "The land around me is tropical rainforest.",
    left: 10,
  },
  {
    clue: "How far is the nearest big city?",
    icon: "🌃",
    cost: 6,
    answer: "The nearest million-person city is 100–300 km away.",
    left: 8,
  },
  {
    clue: "What letter does the place start with?",
    icon: "🔤",
    cost: 9,
    answer: "The name of this place starts with K.",
    left: 5,
  },
];
const SPENT = DEMO.reduce((sum, d) => sum + d.cost, 0);

export function Landing() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStep(DEMO.length);
      return;
    }
    const timer = setInterval(
      () => setStep((s) => (s >= DEMO.length ? 0 : s + 1)),
      1600,
    );
    return () => clearInterval(timer);
  }, []);

  const left = step === 0 ? BOARD.places : DEMO[step - 1].left;
  const budget = 100 - DEMO.slice(0, step).reduce((sum, d) => sum + d.cost, 0);

  return (
    <div>
      <div className="topbar">
        <Wordmark size={20} />
        <Link to="/how-to-play" className="btn btn-ghost">
          How to play
        </Link>
      </div>

      <div className="page">
        <div style={{ padding: "26px 0 10px" }}>
          <Wordmark size={94} drop asLink={false} />
        </div>

        <h1
          style={{
            fontSize: "clamp(24px, 4.2vw, 34px)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            margin: "20px 0 14px",
            maxWidth: "18ch",
          }}
        >
          Hide a place. Leave a clue. See who finds it.
        </h1>
        <p className="lede" style={{ maxWidth: "52ch" }}>
          Hidein is a guessing game about real places. You name the board — the
          whole world, your country, your city — and hide somewhere on it.
          Whoever you send it to gets 100 points to work out where. Asking costs
          points. So does looking.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "28px 0 44px" }}>
          <Link className="btn btn-primary" to="/hide">
            Hide a spot
          </Link>
          <Link className="btn" to="/daily">
            Play today's
          </Link>
        </div>

        {/* The signature: the counter falling as the world runs out of places. */}
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              padding: 20,
            }}
          >
            <div>
              <div className="label">Budget</div>
              <span className="budget">
                {budget}
                <span className="budget-total"> / 100</span>
              </span>
            </div>
            <div>
              <div className="label">Still possible in {BOARD.label}</div>
              <span key={left} className="candidates dropping">
                {left.toLocaleString()}
              </span>
            </div>
          </div>

          <div style={{ padding: "0 20px 20px" }}>
            {DEMO.map((row, i) => (
              <div
                className="revealed"
                key={row.clue}
                style={{
                  opacity: i < step ? 1 : 0.35,
                  transition: "opacity 400ms",
                }}
              >
                <span className="clue-icon">{row.icon}</span>
                <div>
                  <div className="clue-sub">{row.clue}</div>
                  <div className="revealed-answer">
                    {i < step ? row.answer : "· · · · ·"}
                  </div>
                </div>
                <span className="revealed-cost">−{row.cost}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="clue-sub" style={{ marginTop: 14 }}>
          Five questions, {SPENT} points, and {BOARD.label} goes from{" "}
          {BOARD.places} possible places to {DEMO[DEMO.length - 1].left}. You are
          offered three at a time, never the whole list.
        </p>

        {/* The warmth ramp, stated once. */}
        <div style={{ marginTop: 48 }}>
          <p className="eyebrow" style={{ marginBottom: 12 }}>
            A guess costs 5 and comes back warmer or colder
          </p>
          <div className="ramp" style={{ background: RAMP_CSS }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
              fontFamily: "var(--mono)",
              fontSize: 12,
              color: "var(--fog)",
            }}
          >
            <span>Ice cold</span>
            <span>Warm</span>
            <span style={{ color: BRAND.flare }}>Burning</span>
          </div>
        </div>

        <hr className="rule" style={{ margin: "48px 0" }} />

        <div style={{ display: "grid", gap: 28, maxWidth: 620 }}>
          <Explain title="You are told where to look.">
            The hider names the board before anyone starts. On a board you both
            know, "he would absolutely pick the beach one" is a real move — and
            the clues go back to being tie-breakers.
          </Explain>
          <Explain title="Deduction, not sightseeing.">
            No street view, no photographs. You ask about the sea, the altitude,
            the time on the clock there, and reason from the answers.
          </Explain>
          <Explain title="Guessing is hunting.">
            A look costs 5 points and tells you warmer or colder. Probe, react,
            probe again. The budget runs out long before the map does.
          </Explain>
          <Explain title="Nobody has to be online.">
            Send a link. They play whenever. No account, either way.
          </Explain>
          <Explain title="The hider plays too.">
            You score highest when they find you having spent almost everything.
            Hiding somewhere impossible is a losing move.
          </Explain>
        </div>
      </div>
    </div>
  );
}

function Explain({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3>{title}</h3>
      <p className="muted" style={{ margin: 0 }}>
        {children}
      </p>
    </div>
  );
}
